/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * RepoDoc — renders a Markdown file from the public humphreytheodore/person
 * GitHub repo, with the same typographic system as the rest of dotperson.org.
 *
 * The content is fetched at runtime from raw.githubusercontent.com, with a
 * 5-minute in-memory cache. This means the website always renders the latest
 * pushed content — no rebuild required when the protocol changes.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import { fetchProtocolDoc, githubBlobUrl } from "@/lib/githubMarkdown";

type State =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; markdown: string };

export function RepoDoc({ path }: { path: string }) {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchProtocolDoc(path)
      .then((md) => {
        if (!cancelled) setState({ status: "ready", markdown: md });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setState({ status: "error", message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (state.status === "loading") {
    return (
      <div className="border border-hairline bg-paper-2 p-10 text-center">
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
          Loading from github.com/humphreytheodore/person/{path}…
        </p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border border-hairline bg-paper-2 p-10">
        <p className="font-serif text-lg text-ink mb-2">Could not load this document.</p>
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-6">
          {state.message}
        </p>
        <a
          href={githubBlobUrl(path)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-6 py-3 hover:bg-ink hover:text-paper transition-colors"
        >
          View on GitHub →
        </a>
      </div>
    );
  }

  return (
    <article className="prose prose-lg prose-serif max-w-none text-ink-2 prose-headings:text-ink prose-headings:font-serif prose-headings:font-normal prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-a:text-claude prose-a:no-underline hover:prose-a:underline prose-code:text-ink prose-code:bg-paper-2 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-paper-2 prose-pre:text-ink prose-pre:border prose-pre:border-hairline prose-strong:text-ink prose-blockquote:border-l-claude prose-blockquote:text-ink-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSlug]}
        components={{
          // Make repo-internal relative links route within dotperson.org
          // where we have an equivalent page; otherwise open them on GitHub.
          a({ href, children, ...rest }) {
            if (!href) return <a {...rest}>{children}</a>;
            const internal = mapInternalLink(path, href);
            if (internal) {
              return <Link to={internal}>{children}</Link>;
            }
            if (href.startsWith("http")) {
              return (
                <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                  {children}
                </a>
              );
            }
            // Relative file or anchor — link to the corresponding GitHub blob.
            return (
              <a href={resolveRelativeToGithub(path, href)} target="_blank" rel="noopener noreferrer" {...rest}>
                {children}
              </a>
            );
          },
        }}
      >
        {state.markdown}
      </ReactMarkdown>
    </article>
  );
}

/**
 * If a Markdown link points at a file we render under /protocol/*, return
 * the matching internal route; otherwise return null and we'll fall through
 * to a github.com link.
 */
function mapInternalLink(currentPath: string, href: string): string | null {
  // External URLs aren't internal mappings.
  if (href.startsWith("http")) return null;
  // Anchors stay in-page.
  if (href.startsWith("#")) return null;

  // Resolve href relative to the current document's directory.
  const baseDir = currentPath.includes("/") ? currentPath.replace(/\/[^/]+$/, "") : "";
  const resolved = baseDir ? `${baseDir}/${href}` : href;
  const normalised = normaliseRelative(resolved);

  // Mapping from repo path → internal route.
  const map: Record<string, string> = {
    "README.md": "/protocol/repo",
    "ROADMAP.md": "/protocol/roadmap",
    "CONTRIBUTING.md": "/protocol/contributing",
    "schema/README.md": "/protocol/schema",
    "contracts/README.md": "/protocol/contracts",
    "cli/README.md": "/protocol/cli",
    "LICENSE": "/protocol/license",
  };
  return map[normalised] ?? null;
}

function normaliseRelative(p: string): string {
  // Resolve "../" segments
  const parts: string[] = [];
  for (const segment of p.split("/")) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") parts.pop();
    else parts.push(segment);
  }
  return parts.join("/");
}

function resolveRelativeToGithub(currentPath: string, href: string): string {
  const baseDir = currentPath.includes("/") ? currentPath.replace(/\/[^/]+$/, "") : "";
  const resolved = baseDir ? `${baseDir}/${href}` : href;
  return githubBlobUrl(normaliseRelative(resolved));
}
